import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  useCountryNextSteps,
  PHASE_ORDER,
  type CountryNextStepsData,
  type PhaseId,
} from '@/features/next-steps/data';
import { slugToCountry } from '@/features/next-steps/slugs';
import { mapPathFor } from '@/features/map-core/countries';
import { SaveChecklistButton } from '@/features/next-steps/SaveChecklistButton';
import { useSeoMeta, useJsonLd } from '@/hooks/useSeoMeta';
import { AiHelperBubble } from '@/features/ai/AiHelperBubble';
import { LodestarLogo } from '@/components/brand';
import { useCountryRagSlices } from '@/features/ai/rag-context';
import { getPassport, useQuizStore } from '@/features/quiz';
import { useCarOwnership, getCarOwnership, getCarTierColor, CarOwnershipDetail } from '@/features/car-ownership';
import { countriesQuery } from '@/data';
import { useSession } from '@/lib/auth-client';
import {
  downloadChecklistPdf,
  emailChecklistPdf,
  type ChecklistPdfPhase,
} from '@/features/checklist';
import { buildWikiUrl } from '@/features/wiki/architecture';
import type { CountryCode } from '@/features/wiki/architecture';
import { isPublishedCountry } from '@/features/wiki/published-countries';
import { ConsultantCallout } from '@/components/ConsultantCallout';
import { uiLang } from '@/i18n/lang';

const STORAGE_KEY = 'rm:next-steps:v1';

function loadChecked(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: string[] = raw ? JSON.parse(raw) : [];
    // Backward-compat: v0 stored bare item.id. v1 stores composite `phase.item`.
    // Lift legacy ids to the first phase that owns them.
    const lifted = parsed.map((id) => {
      if (id.includes('.')) return id;
      for (const p of PHASES) {
        if (p.items.some((it) => it.id === id)) return `${p.id}.${id}`;
      }
      return id;
    });
    return new Set(lifted);
  } catch {
    return new Set();
  }
}

function saveChecked(ids: Set<string>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // localStorage may be unavailable (quota / private mode). Checklist
    // progress simply won't persist this session.
  }
}

interface Resource {
  label: string;
  url: string;
  note?: string;
}

interface PhaseData {
  id: string;
  timeline: string;
  items: Array<{ id: string }>;
  resources: Resource[];
}

const PHASES: PhaseData[] = [
  {
    id: 'strategy',
    timeline: 'nextSteps.phase.strategy.timeline',
    items: [
      { id: 'visa-research' },
      { id: 'lawyer' },
      { id: 'language' },
      { id: 'tax-home' },
      { id: 'health-zone' },
    ],
    resources: [
      { label: 'r/ItalyExpats', url: 'https://reddit.com/r/ItalyExpats', note: 'nextSteps.resource.community' },
      { label: 'Italian Digital Nomad Visa', url: 'https://www.esteri.it/en/living-and-studying-abroad/studying-abroad/digital-nomad-visa/', note: 'nextSteps.resource.official' },
    ],
  },
  {
    id: 'prep',
    timeline: 'nextSteps.phase.prep.timeline',
    items: [
      { id: 'visa-apply' },
      { id: 'codice-fiscale' },
      { id: 'apartment-search' },
      { id: 'expat-connect' },
      { id: 'health-insurance' },
    ],
    resources: [
      { label: 'Idealista', url: 'https://www.idealista.it', note: 'nextSteps.resource.property' },
      { label: 'Immobiliare.it', url: 'https://www.immobiliare.it', note: 'nextSteps.resource.property' },
      { label: 'Wikicasa', url: 'https://www.wikicasa.it', note: 'nextSteps.resource.property' },
    ],
  },
  {
    id: 'logistics',
    timeline: 'nextSteps.phase.logistics.timeline',
    items: [
      { id: 'arrival-accommodation' },
      { id: 'wise-revolut' },
      { id: 'docs-translation' },
      { id: 'notify-home-bank' },
      { id: 'shipping' },
    ],
    resources: [
      { label: 'Wise', url: 'https://wise.com', note: 'nextSteps.resource.banking' },
      { label: 'Revolut', url: 'https://revolut.com', note: 'nextSteps.resource.banking' },
    ],
  },
  {
    id: 'arrival',
    timeline: 'nextSteps.phase.arrival.timeline',
    items: [
      { id: 'residenza' },
      { id: 'permesso' },
      { id: 'medico-base' },
      { id: 'bank-account' },
      { id: 'ssn' },
    ],
    resources: [
      { label: 'Trova ASL', url: 'https://www.salute.gov.it/portale/asl/homeasl.jsp', note: 'nextSteps.resource.health' },
      { label: 'Fineco Bank', url: 'https://www.finecobank.com', note: 'nextSteps.resource.banking' },
    ],
  },
  {
    id: 'settle',
    timeline: 'nextSteps.phase.settle.timeline',
    items: [
      { id: 'commercialista' },
      { id: 'driver-license' },
      { id: 'utilities' },
      { id: 'community' },
      { id: 'school' },
      // Property-tax obligation — added as a settle-phase item because
      // users only care once they're actually buying / about to buy, not
      // during research. Country-specific text is plumbed via i18n keys
      // so the same item id can render different bodies per locale.
      { id: 'property-tax-obligations' },
    ],
    resources: [
      { label: 'Agenzia delle Entrate', url: 'https://www.agenziaentrate.gov.it', note: 'nextSteps.resource.official' },
      { label: 'ACI', url: 'https://www.aci.it', note: 'nextSteps.resource.official' },
    ],
  },
];

// Italy renders from the pre-existing hardcoded PHASES + i18n keys (legacy v1
// behaviour). For other countries we read the bilingual JSON shipped in
// /data/next-steps-by-country.json and feed it through the same UI shell.
const PHASE_TIMELINE_KEY: Record<PhaseId, string> = {
  strategy: 'nextSteps.phase.strategy.timeline',
  prep: 'nextSteps.phase.prep.timeline',
  logistics: 'nextSteps.phase.logistics.timeline',
  arrival: 'nextSteps.phase.arrival.timeline',
  settle: 'nextSteps.phase.settle.timeline',
};

interface NormalizedPhase {
  id: PhaseId;
  timeline: string;
  items: Array<{ id: string; label: string; detail: string }>;
  resources: Array<{ label: string; url: string; note?: string }>;
}

function normalizeItaly(t: (k: string, opts?: Record<string, unknown>) => string): NormalizedPhase[] {
  // Re-emit the legacy hardcoded PHASES through the new normalised shape so
  // both code paths (IT i18n vs JSON country) flow through the same renderer.
  return PHASES.map((p) => ({
    id: p.id as PhaseId,
    timeline: t(p.timeline, { defaultValue: '' }),
    items: p.items.map((item) => {
      const key = `${p.id}.${item.id}`;
      return {
        id: key,
        label: t(`nextSteps.item.${key}`, { defaultValue: item.id }),
        detail: t(`nextSteps.detail.${key}`, { defaultValue: '' }),
      };
    }),
    resources: p.resources.map((r) => ({
      label: r.label,
      url: r.url,
      note: r.note ? t(r.note, { defaultValue: '' }) : undefined,
    })),
  }));
}

function normalizeCountry(
  country: CountryNextStepsData,
  lang: 'en' | 'ru',
  t: (k: string, opts?: Record<string, unknown>) => string,
): NormalizedPhase[] {
  return PHASE_ORDER.map((id) => {
    const items = country.phases[id].map((item) => ({
      id: `${id}.${item.id}`,
      label: lang === 'ru' ? item.label_ru : item.label_en,
      detail: lang === 'ru' ? item.detail_ru : item.detail_en,
    }));
    const resources = country.resources[id].map((r) => ({
      label: r.label,
      url: r.url,
      note: r.note,
    }));
    return {
      id,
      timeline: t(PHASE_TIMELINE_KEY[id], { defaultValue: '' }),
      items,
      resources,
    };
  });
}

/** Car ownership costs panel — "Getting around" section for NextStepsPage. */
function CarOwnershipSection({
  countryCode,
  lang,
  t,
}: {
  countryCode: string;
  lang: 'en' | 'ru';
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const { data: carFile } = useCarOwnership();
  const carEntry = getCarOwnership(carFile, countryCode);
  if (!carEntry) return null;

  return (
    <details
      className="mt-6 rounded-lg border border-[color:var(--color-ink-100)] bg-[color:var(--color-paper)] open:bg-[color:var(--color-paper-3)]"
      data-testid={`nextsteps-car-${countryCode}`}
    >
      <summary className="flex cursor-pointer items-center justify-between gap-2 px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-[color:var(--color-ink-700)] hover:text-[color:var(--color-ink-900)]">
        <span className="flex items-center gap-1.5">
          🚗
          <span>{t('carOwnership.summaryTitle', { defaultValue: 'Getting around — car costs' })}</span>
          {carEntry.confidence !== 'low' && carEntry.monthly_total_est_eur != null && (
            <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${getCarTierColor(carEntry.tier)}`}>
              {t('carOwnership.chipMonthly', {
                amount: carEntry.monthly_total_est_eur,
                defaultValue: `~€${carEntry.monthly_total_est_eur}/mo`,
              })}
            </span>
          )}
        </span>
        <span aria-hidden="true">▾</span>
      </summary>
      <div className="border-t border-[color:var(--color-ink-100)] p-4">
        <CarOwnershipDetail entry={carEntry} countryCode={countryCode} lang={lang} />
      </div>
    </details>
  );
}

/**
 * L5 — branded PDF export panel.
 *
 * Two affordances side-by-side, no modal:
 *   1. "Download PDF" — renders + triggers a download (synchronous from the
 *      user's POV; busy state covers the ~300 ms layout phase on slower
 *      laptops so the button can't be double-clicked into two downloads).
 *   2. "Email checklist" — collapses to an inline email form; on submit we
 *      render the same PDF and POST to /api/checklist/email. Toast surfaces
 *      success/failure — never silent.
 *
 * Why a self-contained component: NextStepsPage already pushes ~600 lines;
 * this keeps the new logic readable and lets the tests target a single
 * data-testid prefix without grepping the whole page.
 */
/**
 * Highlighted callout above the checklist that links into the country
 * encyclopedia. Surfaces only for the 4 fully-published countries
 * (IT / PT / ES / AE). Silent for all 26 others so the checklist UI stays
 * unchanged where there's no companion guide to link out to.
 */
function CountryGuideCallout({
  countryCode,
  countryName,
  lang,
}: {
  countryCode: string;
  countryName: string;
  lang: 'en' | 'ru';
}) {
  const { t } = useTranslation();
  if (!isPublishedCountry(countryCode)) return null;
  const cc = countryCode as CountryCode;
  const topics: Array<{ topic: 'visa' | 'taxes' | 'cost-of-living' | 'property' | 'healthcare' | 'banking'; labelKey: string; fallback: string }> = [
    { topic: 'visa',           labelKey: 'nextSteps.guide.visa',     fallback: 'Visa' },
    { topic: 'taxes',          labelKey: 'nextSteps.guide.taxes',    fallback: 'Taxes' },
    { topic: 'cost-of-living', labelKey: 'nextSteps.guide.cost',     fallback: 'Cost of living' },
    { topic: 'banking',        labelKey: 'nextSteps.guide.banking',  fallback: 'Banking' },
    { topic: 'property',       labelKey: 'nextSteps.guide.property', fallback: 'Property' },
    { topic: 'healthcare',     labelKey: 'nextSteps.guide.healthcare', fallback: 'Healthcare' },
  ];
  return (
    <aside
      aria-labelledby="next-steps-guide-callout"
      className="mt-6 rounded-lg border border-[color:var(--color-terra-200)] bg-[color:var(--color-paper-2)] px-4 py-3.5"
      data-testid="nextsteps-country-guide"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p
          id="next-steps-guide-callout"
          className="font-mono text-[11px] uppercase tracking-wider text-[color:var(--color-terra-700)]"
        >
          {t('nextSteps.guide.eyebrow', { defaultValue: 'Read the full guide' })}
        </p>
        <Link
          to={buildWikiUrl({ lang, cc })}
          className="font-mono text-[11px] uppercase tracking-wider text-[color:var(--color-ink-700)] hover:text-[color:var(--color-terra-700)]"
        >
          {t('nextSteps.guide.hubLink', {
            defaultValue: '{{country}} encyclopedia →',
            country: countryName,
          })}
        </Link>
      </div>
      <ul className="mt-2.5 flex flex-wrap gap-1.5">
        {topics.map(({ topic, labelKey, fallback }) => (
          <li key={topic}>
            <Link
              to={buildWikiUrl({ lang, cc, topic })}
              className="inline-flex items-center rounded border border-[color:var(--color-ink-100)] bg-[color:var(--color-paper)] px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-[color:var(--color-ink-700)] hover:border-[color:var(--color-terra-300)] hover:text-[color:var(--color-terra-700)]"
            >
              {t(labelKey, { defaultValue: fallback })}
            </Link>
          </li>
        ))}
      </ul>
    </aside>
  );
}

function ExportPdfControls({
  countryCode,
  countryName,
  flag,
  lang,
  phases,
  pdfStrings,
  t,
  defaultEmail,
}: {
  countryCode: string;
  countryName: string;
  flag: string | null;
  lang: 'en' | 'ru';
  phases: ChecklistPdfPhase[];
  pdfStrings: import('@/features/checklist').ChecklistPdfStrings;
  t: (key: string, opts?: Record<string, unknown>) => string;
  defaultEmail: string;
}) {
  const [downloading, setDownloading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  // `emailDraft === null` means "user hasn't typed yet" — we then fall back
  // to `defaultEmail` which may resolve async from useSession. Once the user
  // types (even an empty string) we honour their input verbatim. This dodges
  // the cascading-renders eslint rule that fires on setState-in-useEffect.
  const [emailDraft, setEmailDraft] = useState<string | null>(null);
  const emailValue = emailDraft ?? defaultEmail;
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState<
    null | { kind: 'success' | 'error'; text: string }
  >(null);

  // Auto-dismiss the toast — keeps the UI uncluttered without forcing the
  // user to hunt for a close button.
  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 4500);
    return () => window.clearTimeout(id);
  }, [toast]);

  const filename = `lodestar-${countryCode.toUpperCase()}-checklist.pdf`;

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setToast(null);
    try {
      await downloadChecklistPdf(
        {
          countryName,
          flag,
          phases,
          strings: pdfStrings,
        },
        filename,
      );
    } catch (err) {
      // Should only fire on a hard renderer crash (e.g. OOM with a huge
      // checklist). Tell the user — silently failing on a download is the
      // worst possible UX (they'll wonder if their browser is broken).
      console.error('PDF download failed', err);
      setToast({
        kind: 'error',
        text: t('nextSteps.pdf.downloadError', {
          defaultValue: 'Could not generate the PDF. Please try again.',
        }),
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (sending) return;
    if (!emailValue.includes('@')) return;
    setSending(true);
    setToast(null);
    try {
      const result = await emailChecklistPdf(
        { countryName, flag, phases, strings: pdfStrings },
        {
          email: emailValue,
          countryCode,
          countryName,
          lang,
          filename,
        },
      );
      if (result.ok) {
        setEmailOpen(false);
        setToast({
          kind: 'success',
          text: t('nextSteps.pdf.emailSuccess', {
            email: emailValue,
            defaultValue: 'Sent to {{email}} — check your inbox.',
          }),
        });
      } else {
        const message =
          result.error === 'rate_limited'
            ? t('nextSteps.pdf.emailRateLimited', {
                defaultValue: 'Too many sends from your network. Try again in 15 minutes.',
              })
            : t('nextSteps.pdf.emailError', {
                defaultValue: 'Could not send the email. Please try again.',
              });
        setToast({ kind: 'error', text: message });
      }
    } catch (err) {
      console.error('PDF email send failed', err);
      setToast({
        kind: 'error',
        text: t('nextSteps.pdf.emailError', {
          defaultValue: 'Could not send the email. Please try again.',
        }),
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading || phases.length === 0}
        className="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--color-ink-200)] bg-[color:var(--color-paper)] px-3 py-1.5 font-mono text-xs font-semibold uppercase tracking-wider text-[color:var(--color-ink-600)] transition hover:bg-[color:var(--color-ink-50)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-ink-400)] disabled:opacity-60"
        data-testid={`checklist-pdf-download-${countryCode.toUpperCase()}`}
        title={t('nextSteps.pdf.downloadHint', {
          defaultValue: 'Download a branded PDF of this checklist',
        })}
      >
        <span aria-hidden="true">⬇</span>
        {downloading
          ? t('nextSteps.pdf.downloading', { defaultValue: 'Generating…' })
          : t('nextSteps.pdf.download', { defaultValue: 'Download PDF' })}
      </button>

      {emailOpen ? (
        <form
          onSubmit={handleEmailSubmit}
          className="flex flex-wrap items-center gap-2"
          data-testid={`checklist-pdf-email-form-${countryCode.toUpperCase()}`}
        >
          <input
            type="email"
            value={emailValue}
            onChange={(e) => setEmailDraft(e.target.value)}
            placeholder={t('nextSteps.pdf.emailPlaceholder', {
              defaultValue: 'you@example.com',
            })}
            required
            className="min-w-[180px] flex-1 rounded border border-[color:var(--color-ink-200)] bg-[color:var(--color-paper)] px-3 py-1.5 font-mono text-xs text-[color:var(--color-ink-900)] placeholder-[color:var(--color-ink-400)] focus:outline-none focus:ring-1 focus:ring-[color:var(--color-terra-600)]"
            data-testid={`checklist-pdf-email-input-${countryCode.toUpperCase()}`}
          />
          <button
            type="submit"
            disabled={sending || !emailValue.includes('@')}
            className="shrink-0 rounded bg-[color:var(--color-terra-600)] px-3 py-1.5 font-mono text-xs font-semibold uppercase tracking-wider text-white hover:bg-[color:var(--color-terra-700)] disabled:opacity-60"
          >
            {sending
              ? t('nextSteps.pdf.sending', { defaultValue: 'Sending…' })
              : t('nextSteps.pdf.send', { defaultValue: 'Send to email' })}
          </button>
          <button
            type="button"
            onClick={() => setEmailOpen(false)}
            className="font-mono text-xs text-[color:var(--color-ink-400)] hover:text-[color:var(--color-ink-700)]"
            aria-label={t('common.cancel', { defaultValue: 'Cancel' })}
          >
            ✕
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setEmailOpen(true)}
          disabled={phases.length === 0}
          className="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--color-terra-500)] bg-[color:var(--color-paper)] px-3 py-1.5 font-mono text-xs font-semibold uppercase tracking-wider text-[color:var(--color-terra-700)] transition hover:bg-[color:var(--color-terra-50)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-terra-600)] disabled:opacity-60"
          data-testid={`checklist-pdf-email-btn-${countryCode.toUpperCase()}`}
          title={t('nextSteps.pdf.emailHint', {
            defaultValue: 'Send the PDF to your inbox',
          })}
        >
          <span aria-hidden="true">✉</span>
          {t('nextSteps.pdf.emailCta', { defaultValue: 'Email checklist' })}
        </button>
      )}

      {/* Inline toast — rendered next to the controls (not a fixed overlay)
          so it never covers the checklist on small screens. role=status so
          screen readers announce the result without taking focus. */}
      {toast && (
        <span
          role="status"
          aria-live="polite"
          data-testid={`checklist-pdf-toast-${toast.kind}`}
          className={`font-mono text-[11px] ${
            toast.kind === 'success'
              ? 'text-[color:var(--color-moss-700)]'
              : 'text-[color:var(--color-terra-700)]'
          }`}
        >
          {toast.kind === 'success' ? '✓ ' : '⚠ '}
          {toast.text}
        </span>
      )}
    </>
  );
}

export default function NextStepsPage() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const { slug } = useParams<{ slug?: string }>();
  // Prefer route slug (clean URL /checklists/<slug>); fall back to legacy
  // ?country=XX query param for backwards compatibility with old shared links.
  const countryParam =
    slugToCountry(slug) ?? (searchParams.get('country') ?? 'IT').toUpperCase();
  const lang: 'en' | 'ru' = uiLang(i18n.language);

  const countryQuery = useCountryNextSteps(countryParam);
  const isItaly = countryParam === 'IT';
  // "Back to map" only makes sense for countries that have one; everyone else
  // goes back to the quiz results. Registry decides, not a country literal.
  const mapPath = mapPathFor(countryParam);

  const phases: NormalizedPhase[] = useMemo(() => {
    if (isItaly) return normalizeItaly(t);
    if (countryQuery.data) return normalizeCountry(countryQuery.data, lang, t);
    return [];
  }, [isItaly, countryQuery.data, lang, t]);

  const countryName = useMemo(() => {
    if (isItaly) return null;
    if (!countryQuery.data) return countryParam;
    return lang === 'ru' ? countryQuery.data.name_ru : countryQuery.data.name_en;
  }, [isItaly, countryQuery.data, lang, countryParam]);

  // Country lookup — needed for the flag emoji + the resolved name used in
  // the PDF (Italy uses 'name_en'/'name_ru' from countries.json, not the
  // null fallback we ship through the header copy).
  const { data: countriesFile } = useQuery(countriesQuery());
  const countryRecord = useMemo(
    () => countriesFile?.countries.find((c) => c.code === countryParam),
    [countriesFile, countryParam],
  );
  const countryFlag = countryRecord?.flag_emoji ?? null;
  const resolvedCountryName =
    countryName ??
    (countryRecord ? (lang === 'ru' ? countryRecord.name_ru : countryRecord.name_en) : countryParam);

  // Session for default email on the "Email checklist" form.
  const { data: session } = useSession();

  // RAG-lite for the AI helper: the country slug determines what knowledge
  // slice to ship. Passport is pulled from the quiz store if the user took
  // the quiz earlier in the session — silently optional for cold visitors.
  const quizAnswers = useQuizStore((s) => s.answers);
  const passportOption = getPassport(quizAnswers);
  const ragSlices = useCountryRagSlices([countryParam], passportOption, lang);

  const [checked, setChecked] = useState<Set<string>>(loadChecked);
  const [open, setOpen] = useState<Set<string>>(new Set(['strategy']));

  const toggle = (id: string) => {
    const next = new Set(checked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setChecked(next);
    saveChecked(next);
  };

  const togglePhase = (id: string) => {
    const next = new Set(open);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpen(next);
  };

  // Print prep: when the user fires the print dialog, force-open every
  // phase so the printout contains the FULL checklist instead of just
  // whatever the user happened to expand on screen. Restore on afterprint.
  // Without this, collapsed phases are not in the DOM at all (the page
  // uses {isOpen && <div>...</div>}), so the print stylesheet can't reveal
  // them — we need state-level expansion ahead of time.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let snapshot: Set<string> | null = null;
    const onBefore = () => {
      snapshot = new Set(open);
      setOpen(new Set(phases.map((p) => p.id)));
    };
    const onAfter = () => {
      if (snapshot) setOpen(snapshot);
      snapshot = null;
    };
    window.addEventListener('beforeprint', onBefore);
    window.addEventListener('afterprint', onAfter);
    return () => {
      window.removeEventListener('beforeprint', onBefore);
      window.removeEventListener('afterprint', onAfter);
    };
  }, [phases, open]);

  const totalItems = phases.reduce((s, p) => s + p.items.length, 0);
  const validKeys = new Set(phases.flatMap((p) => p.items.map((item) => item.id)));
  const checkedCount = [...checked].filter((id) => validKeys.has(id)).length;

  const isLoadingCountry = !isItaly && countryQuery.isLoading;
  const isMissingCountry = !isItaly && !countryQuery.isLoading && !countryQuery.data;

  const title = isItaly
    ? t('nextSteps.title', { defaultValue: 'Moving to Italy — next steps' })
    : t('nextSteps.titleCountry', {
        country: countryName ?? countryParam,
        defaultValue: 'Moving to {{country}} — next steps',
      });

  // SEO: per-checklist <title>/description so /checklists/<slug> indexes as
  // its own destination, not a clone of the landing page. Description is
  // intentionally specific — vague descriptions hurt CTR.
  const subtitle = t('nextSteps.subtitle', {
    defaultValue:
      'A practical checklist from decision to settlement. Your progress is saved locally.',
  });
  useSeoMeta({
    title,
    description: `${subtitle} ${countryName ?? countryParam} — visa, residency, banking, healthcare, schools, settling-in.`,
  });

  // HowTo JSON-LD — gives Google rich results eligibility on this URL. We
  // strip the empty-load cycle (phases=[] before data resolves) so we don't
  // emit a malformed schema for crawlers during prerender.
  const howToJson = useMemo(() => {
    if (phases.length === 0) return null;
    const step = phases.map((phase) => ({
      '@type': 'HowToStep',
      name: t(`nextSteps.phase.${phase.id}.title`, { defaultValue: phase.id }),
      ...(phase.timeline ? { timeRequired: phase.timeline } : {}),
      itemListElement: phase.items.map((item) => ({
        '@type': 'HowToDirection',
        text: item.detail ? `${item.label}. ${item.detail}` : item.label,
      })),
    }));
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: title,
      description: subtitle,
      step,
    });
  }, [phases, title, subtitle, t]);
  useJsonLd(howToJson, `checklist-howto-${countryParam}`);

  // PDF data: enrich phases with the user's done-state so the rendered PDF
  // mirrors what's on screen (no "all unchecked" surprise after the user
  // worked through half the list). Strings are bundled here so the PDF
  // can be rendered from a non-React entry point (worker, retry) too.
  const pdfPhases: ChecklistPdfPhase[] = useMemo(
    () =>
      phases.map((p) => ({
        id: p.id,
        title: t(`nextSteps.phase.${p.id}.title`, { defaultValue: p.id }),
        timeline: p.timeline,
        items: p.items.map((it) => ({
          id: it.id,
          label: it.label,
          detail: it.detail,
          done: checked.has(it.id),
        })),
        resources: p.resources,
      })),
    [phases, checked, t],
  );

  const pdfStrings = useMemo(() => {
    const dateStr = new Date().toLocaleDateString(
      lang === 'ru' ? 'ru-RU' : 'en-GB',
      { year: 'numeric', month: 'short', day: 'numeric' },
    );
    const recipient = session?.user?.email ?? t('nextSteps.pdf.anonymous', {
      defaultValue: 'anonymous',
    });
    return {
      documentTitle: title,
      subtitle,
      generatedFor: t('nextSteps.pdf.generatedFor', {
        recipient,
        date: dateStr,
        defaultValue: 'Generated for {{recipient}} on {{date}}',
      }),
      resourcesLabel: t('nextSteps.resources', { defaultValue: 'Resources' }),
      disclaimer: t('nextSteps.disclaimer', {
        defaultValue:
          'This checklist is a general guide. Requirements vary by nationality, visa type, and comune. Consult an immigration lawyer for your specific situation.',
      }),
      footerUrl: 'lodestar.place',
      pageNumber: (page: number, total: number) =>
        t('nextSteps.pdf.pageOf', {
          page,
          total,
          defaultValue: 'Page {{page}} of {{total}}',
        }),
    };
  }, [lang, session, t, title, subtitle]);

  return (
    <div className="min-h-screen bg-[color:var(--color-paper)]">
      {/* Print-only header — Lodestar wordmark + title + date + URL. The
          screen-mode header lives in the SimplePage wrapper and is hidden
          via @media print in index.css. */}
      <header className="hidden print:block print:mb-6 print:border-b print:border-ink-300 print:pb-3">
        <div className="flex items-center justify-between">
          <LodestarLogo size={22} />
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-500">
            {new Date().toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-GB', {
              year: 'numeric', month: 'short', day: 'numeric',
            })}{' · '}lodestar.place
          </span>
        </div>
        <p className="mt-2 font-display text-xl italic text-ink-900">{title}</p>
      </header>
      <div className="mx-auto max-w-2xl px-4 py-10 print:max-w-none print:px-0 print:py-0">
        {/* Header */}
        <div className="mb-2 print:hidden">
          <Link
            to={mapPath ?? '/quiz/result'}
            className="font-mono text-[11px] tracking-wider text-[color:var(--color-ink-400)] hover:text-[color:var(--color-ink-700)]"
          >
            ← {mapPath
              ? t('nextSteps.backToMap', { defaultValue: 'Back to map' })
              : t('nextSteps.backToResult', { defaultValue: 'Back to quiz results' })}
          </Link>
        </div>
        <h1 className="font-display text-3xl font-medium italic text-[color:var(--color-ink-900)]">
          {title}
        </h1>
        <p className="mt-2 font-mono text-xs tracking-wider text-[color:var(--color-ink-500)]">
          {t('nextSteps.subtitle', {
            defaultValue: 'A practical checklist from decision to settlement. Your progress is saved locally.',
          })}
        </p>

        {isLoadingCountry && (
          <p className="mt-6 font-mono text-xs text-[color:var(--color-ink-500)]">
            {t('loading.default', { defaultValue: 'Loading…' })}
          </p>
        )}

        {isMissingCountry && (
          <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {t('nextSteps.noData', {
              country: countryParam,
              defaultValue:
                'We don\'t have a relocation checklist for {{country}} yet. The 9 countries with checklists are AE, PT, ES, CY, TH, MY, ID, GR, MU (plus IT).',
            })}
          </div>
        )}

        {/* Progress bar */}
        {phases.length > 0 && (
          <div className="mt-5 flex items-center gap-3">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[color:var(--color-ink-100)]">
              <div
                className="h-full rounded-full bg-[color:var(--color-terra-600)] transition-all duration-300"
                style={{ width: `${totalItems > 0 ? (checkedCount / totalItems) * 100 : 0}%` }}
              />
            </div>
            <span className="shrink-0 font-mono text-[11px] tabular-nums text-[color:var(--color-ink-500)]">
              {checkedCount}/{totalItems}
            </span>
          </div>
        )}

        {/* L4 — Save checklist (lead magnet → magic-link soft signup)
            L5 — Real branded PDF export (download + email-to-self). The
            data-print-expand-all attr is kept for the legacy @media print
            fallback (ctrl-P from the page still works for power users). */}
        {phases.length > 0 && (
          <div
            data-print-expand-all
            className="mt-4 flex flex-wrap items-center gap-3 print:hidden"
          >
            <SaveChecklistButton
              country={countryParam}
              countryName={countryName ?? undefined}
            />
            <ExportPdfControls
              countryCode={countryParam}
              countryName={resolvedCountryName}
              flag={countryFlag}
              lang={lang}
              phases={pdfPhases}
              pdfStrings={pdfStrings}
              t={t}
              defaultEmail={session?.user?.email ?? ''}
            />
          </div>
        )}

        {/* Wiki guide shortcut — surfaces the country encyclopedia entry
            points for users mid-checklist. Renders only for countries with
            a fully-published guide; silent for all 26 others. */}
        <CountryGuideCallout
          countryCode={countryParam}
          countryName={resolvedCountryName}
          lang={lang}
        />

        {/* Phases */}
        <div className="mt-8 space-y-3">
          {phases.map((phase) => {
            const isOpen = open.has(phase.id);
            const phaseChecked = phase.items.filter((item) => checked.has(item.id)).length;
            const allDone = phaseChecked === phase.items.length;

            return (
              <section
                key={phase.id}
                className="overflow-hidden rounded-lg border border-[color:var(--color-ink-100)]"
              >
                <button
                  type="button"
                  onClick={() => togglePhase(phase.id)}
                  className="flex w-full items-center justify-between bg-[color:var(--color-paper-2)] px-4 py-3 text-left"
                >
                  <div>
                    <span
                      className={`font-display text-sm italic ${
                        allDone
                          ? 'text-[color:var(--color-moss-600)]'
                          : 'text-[color:var(--color-ink-900)]'
                      }`}
                    >
                      {allDone && <span className="mr-1.5 not-italic">✓</span>}
                      {t(`nextSteps.phase.${phase.id}.title`, { defaultValue: phase.id })}
                    </span>
                    {phase.timeline && (
                      <span className="ml-2 font-mono text-[10px] text-[color:var(--color-ink-400)]">
                        {phase.timeline}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-mono text-[10px] text-[color:var(--color-ink-400)]">
                      {phaseChecked}/{phase.items.length}
                    </span>
                    <span className="text-[color:var(--color-ink-400)]">{isOpen ? '▲' : '▼'}</span>
                  </div>
                </button>

                {isOpen && (
                  <div className="divide-y divide-[color:var(--color-ink-50)] bg-[color:var(--color-paper)] px-4">
                    {/* Checklist items */}
                    <ul className="py-3 space-y-2">
                      {phase.items.map((item) => {
                        const isDone = checked.has(item.id);
                        return (
                          <li key={item.id}>
                            <label className="flex cursor-pointer items-start gap-3">
                              <input
                                type="checkbox"
                                checked={isDone}
                                onChange={() => toggle(item.id)}
                                className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[color:var(--color-terra-600)]"
                              />
                              <div>
                                <span
                                  className={`font-mono text-xs ${
                                    isDone
                                      ? 'line-through text-[color:var(--color-ink-400)]'
                                      : 'text-[color:var(--color-ink-800)]'
                                  }`}
                                >
                                  {item.label}
                                </span>
                                {item.detail && (
                                  <p className="mt-0.5 font-mono text-[10px] text-[color:var(--color-ink-400)]">
                                    {item.detail}
                                  </p>
                                )}
                              </div>
                            </label>
                          </li>
                        );
                      })}
                    </ul>

                    {/* Resources */}
                    {phase.resources.length > 0 && (
                      <div className="py-3">
                        <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-[color:var(--color-ink-400)]">
                          {t('nextSteps.resources', { defaultValue: 'Resources' })}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {phase.resources.map((r) => (
                            <a
                              key={r.url}
                              href={r.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 rounded border border-[color:var(--color-ink-100)] px-2.5 py-1 font-mono text-[10px] text-[color:var(--color-ink-600)] hover:border-[color:var(--color-ink-300)] hover:text-[color:var(--color-ink-900)]"
                            >
                              {r.label}
                              {r.note && (
                                <span className="text-[color:var(--color-ink-400)]">
                                  · {r.note}
                                </span>
                              )}
                              <span aria-hidden="true" className="text-[color:var(--color-ink-300)]">↗</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Per-phase specialist CTA */}
                    <div className="py-3">
                      <ConsultantCallout
                        source={`checklist_${phase.id}`}
                        variant="inline"
                      />
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>

        {/* M8 — escape valve for users who want a real person, not data. */}
        {phases.length > 0 && (
          <div className="mt-8">
            <ConsultantCallout source="next_steps" />
          </div>
        )}

        {/* Car ownership costs — "Getting around" section. Only renders
            when data is available for the selected country. Mirrors the
            same <details> pattern used in CountryScoreCard so users get
            the full breakdown (purchase taxes, annual costs, fuel, DL
            exchange) in one scannable panel. */}
        <CarOwnershipSection countryCode={countryParam} lang={lang} t={t} />

        {/* Footer note */}
        <p className="mt-8 font-mono text-[10px] text-[color:var(--color-ink-400)]">
          {t('nextSteps.disclaimer', {
            defaultValue:
              'This checklist is a general guide. Requirements vary by nationality, visa type, and comune. Consult an immigration lawyer for your specific situation.',
          })}
        </p>
      </div>

      {/* M5 — AI helper for checklist items. The context is the country + a
          digest of phase titles + items so the model can answer "what is a
          codice fiscale" or "do I need a translator for this step" without
          us shipping a separate FAQ per country. `knowledge` carries the
          property-tax + passport-treatment slice so questions about taxes
          on bought property are answered from verified data. */}
      {phases.length > 0 && (
        <AiHelperBubble
          surface="next_steps"
          context={{
            country: countryName ?? countryParam,
            countryCode: countryParam,
            phases: phases.map((p) => ({
              id: p.id,
              title: t(`nextSteps.phase.${p.id}.title`, { defaultValue: p.id }),
              timeline: p.timeline,
              items: p.items.map((it) => it.label),
            })),
            knowledge: ragSlices,
          }}
        />
      )}
    </div>
  );
}
